import React, { useState } from 'react';
import { Task } from '../Interfaces';
import { formatDistanceToNow } from 'date-fns';
import { Modal, Button } from 'react-bootstrap';
import NewTaskModal from '../../../modals/NewTaskModal';

interface Props {
  tasks: Task[];
  handleDeleteTask: (task: Task) => void;
  setSelectedTask: (task: Task) => void;
  searchTerm: string;
  currentJob: any;
  onTaskUpdated?: (updatedTask: Task) => void;
  onTaskClick?: (task: Task) => void;
}

const TaskList: React.FC<Props> = ({ 
  tasks, 
  handleDeleteTask, 
  setSelectedTask, 
  searchTerm, 
  currentJob,
  onTaskUpdated,
  onTaskClick 
}): JSX.Element => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  const formatDate = (timestamp: any) => {
    try {
      let date: Date;
      
      // Handle null, undefined, or empty values
      if (!timestamp) {
        return 'No date';
      }
      
      // Handle Firestore Timestamp objects
      if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle JavaScript Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle numeric timestamps (milliseconds)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle string timestamps
      else if (typeof timestamp === 'string') {
        // Handle ISO strings and other common formats
        const parsedDate = new Date(timestamp);
        if (isNaN(parsedDate.getTime())) {
          return 'Invalid date format';
        }
        date = parsedDate;
      }
      // Handle objects with seconds/nanoseconds (Firestore format)
      else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle any other object format
      else if (typeof timestamp === 'object') {
        // Try to extract common timestamp properties
        if (timestamp.toISOString) {
          date = new Date(timestamp.toISOString());
        } else if (timestamp._seconds) {
          date = new Date(timestamp._seconds * 1000);
        } else {
          return 'Unknown date format';
        }
      }
      else {
        return 'Unsupported date type';
      }
      
      // Validate the final date
      if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error, 'timestamp:', timestamp);
      return 'Date error';
    }
  };

  const handleDelete = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setTaskToDelete(task);
    setShowDeleteConfirmation(true);
  };

  const handleEdit = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setTaskToEdit(task);
    setIsEditModalOpen(true);
  };

  const confirmDelete = () => {
    if (taskToDelete) {
      handleDeleteTask(taskToDelete);
    }
    setShowDeleteConfirmation(false);
    setTaskToDelete(null);
  };

  const handleTaskClick = (task: Task) => {
    if (onTaskClick) {
      onTaskClick(task);
    } else {
      setSelectedTask(task);
    }
  };

  // Helper function to get task description from either field
  const getTaskDescription = (task: Task): string => {
    return task.description || task.task || '';
  };

  return (
    <>
      <div className="d-flex flex-column h-100">
        {tasks.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1">
            <div className="text-center">
              <i className="bi bi-list-task text-muted fs-2x mb-3"></i>
              <p className="text-muted fw-semibold mb-0">
                {searchTerm ? 'No tasks match your search' : 'No tasks found'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-grow-1 overflow-auto pe-3" style={{ height: 0 }}>
            {tasks.map((task) => (
              <div
                key={task.id}
                className="card border mb-3 task-item"
                style={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  pointerEvents: 'auto',
                  zIndex: 1
                }}
                onClick={() => handleTaskClick(task)}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-start">
                    {/* Primary Icon - Speech Bubble with Feed Count */}
                    <div className="flex-shrink-0 me-3" style={{ padding: '6px 2px 2px 2px' }}>
                      <div className="symbol symbol-35px" style={{ position: 'relative' }}>
                        <div className="symbol-label bg-light-success">
                          <i className="bi bi-chat-dots text-success fs-6"></i>
                        </div>
                        {task.feed && task.feed.length > 0 && (
                          <div className="symbol-badge badge badge-circle badge-success" style={{ 
                            fontSize: '0.7rem',
                            minWidth: '16px',
                            height: '16px',
                            lineHeight: '16px',
                            position: 'absolute',
                            top: '0px',
                            right: '-2px',
                            zIndex: 1
                          }}>
                            {task.feed.length}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <h6 className="text-gray-900 fw-bold mb-0 text-truncate" style={{ maxWidth: '70%' }}>
                          {task.title}
                        </h6>
                        <small className="text-muted px-2 py-1" style={{ 
                          backgroundColor: 'var(--bs-gray-100)', 
                          borderRadius: 'var(--bs-border-radius-sm)',
                          fontSize: '0.75rem'
                        }}>
                          {task.lastEdited ? formatDate(task.lastEdited) : 'No updates'}
                        </small>
                      </div>
                      
                      {/* Task Description */}
                      {getTaskDescription(task) && (
                        <p className="text-gray-600 mb-0 task-description" style={{
                          fontSize: '0.875rem',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word'
                        }}>
                          {getTaskDescription(task)}
                        </p>
                      )}
                    </div>
                    
                    <div className="d-flex flex-shrink-0 align-items-center">
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light-success me-1"
                        onClick={(e) => { e.stopPropagation(); handleEdit(e, task); }}
                        title="Edit Task"
                      >
                        <i className="bi bi-pencil fs-6"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light btn-active-light-danger"
                        onClick={(e) => { e.stopPropagation(); handleDelete(e, task); }}
                        title="Delete Task"
                      >
                        <i className="bi bi-trash fs-6"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteConfirmation} 
        onHide={() => setShowDeleteConfirmation(false)}
        centered
        className="fade"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold text-gray-900">
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pb-4">
          <div className="text-gray-600 fs-6">
            Are you sure you want to delete the task <strong>"{taskToDelete?.title}"</strong>?
            <br />
            This action cannot be undone.
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="light"
            className="me-3"
            onClick={() => setShowDeleteConfirmation(false)}
          >
            <i className="bi bi-x me-1"></i>
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={confirmDelete}
          >
            <i className="bi bi-trash me-1"></i>
            Delete Task
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Task Modal */}
      {taskToEdit && (
        <NewTaskModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setTaskToEdit(null);
          }}
          currentJob={currentJob}
          editTask={taskToEdit}
          onNewTaskAdded={(updatedTask) => {
            if (onTaskUpdated) {
              onTaskUpdated(updatedTask);
            }
          }}
        />
      )}

      <style>
        {`
        .task-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15) !important;
        }
        
        .text-truncate-2-lines {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
          max-height: 2.8em;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        /* Scrollbar styling */
        .overflow-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-auto::-webkit-scrollbar-track {
          background: var(--bs-gray-200);
          border-radius: var(--bs-border-radius-pill);
        }
        
        .overflow-auto::-webkit-scrollbar-thumb {
          background: var(--bs-gray-400);
          border-radius: var(--bs-border-radius-pill);
        }
        
        .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: var(--bs-gray-500);
        }
        
        .btn-icon.btn-sm {
          width: 30px;
          height: 30px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        `}
      </style>
    </>
  );
};

export default TaskList;